import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Form, Layout } from 'antd';
import SignersStep from '../../components/contract/SignersStep';
import FormPreview from '../../components/formPreview/FormPreview';
import PaymentStep from '../../components/contract/PaymentStep';
import { CONTRACT_FORM_DATA, GET_DRAFT, SOURCE_URL } from '../../state/lease_contract/types';
import alertError from '../../components/alerts/AlertErrorForm';
import { SEND_MONITORING } from '../../state/dashboard/types';
import { useParams } from 'react-router-dom';
import { RESOURCE } from '../../config/constants/alerts';
import { EXTERNAL_URL } from '../../config/constants/urls';
import PropTypes from 'prop-types';
import { trackPageView, trackEvent, setUserProperties, trackFormAbandonment } from '../../helpers/analytics';
import PropertyStep from '../../components/contract/PropertyStep';
import AccountStep from '../../components/contract/AccountStep';
import CreateContractHeader from '../../components/create_contract/header/index';
import CreateContractFooter from '../../components/create_contract/footer/index';
import WelcomeForm from '../../components/modals/WelcomeForm';

const { Content } = Layout;

const CreateContract = ({
  updateForm,
  contractFormData,
  sendStats,
  getDraft,
  setSourceUrlInReduce,
  sourceUrl,
  images,
}) => {

  const { params } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [form] = Form.useForm();
  const [contractIsCounted, setContractIsCounted] = useState(false);
  const [reviewIsCounted, setReviewIsCounted] = useState(false);
  const [webpayIsCounted, setWebpayIsCounted] = useState(false);
  const [welcomeModalVisible, setWelcomeModalVisible] = useState(true);
  const activeStepRef = useRef(0);

  const STEP_NAMES = [
    'Información firmantes',
    'Inmueble y condiciones',
    'Contrato y cuenta',
    'Revisión',
    'Pago',
  ];

  useEffect(() => {
    sessionStorage.removeItem('contractId');
    const referrerUrl = document.referrer;
    const urlDomain = referrerUrl.split('/')[2];

    // Track page view in Google Analytics
    trackPageView('/contract/make_contract', 'Crear Contrato');

    // Set source URL as user property for attribution
    const source = urlDomain || EXTERNAL_URL.DEFAULT;
    setUserProperties({ source_url: source });

    if (urlDomain) {
      //Set the referrer url where the user come from.
      setSourceUrlInReduce(urlDomain);
    } else {
      //Set default url
      setSourceUrlInReduce(EXTERNAL_URL.DEFAULT);
    };

    //if draft token exists, get draft data
    if (params !== undefined) {
      getDraft(params);
      trackEvent('Contract Form', 'draft_loaded', 'Con borrador');
    } else {
      trackEvent('Contract Form', 'form_started', 'Sin borrador');
    }

    // Set up form abandonment tracking
    const cleanupAbandonment = trackFormAbandonment(
      () => activeStepRef.current,
      STEP_NAMES,
    );

    return () => {
      cleanupAbandonment();
    };
  }, []);

  useEffect(() => {
    if (contractFormData !== null) {
      form.setFieldsValue(contractFormData);
    }
  }, [contractFormData]);

  //send dashboard count
  useEffect(() => {
    // Keep ref in sync for abandonment tracking
    activeStepRef.current = activeStep;

    // Track form step view in Google Analytics
    trackEvent('Contract Form', 'step_view', STEP_NAMES[activeStep], activeStep);

    switch (activeStep) {
      case 0:
        if (!contractIsCounted) {
          sendStats('contract');
          setContractIsCounted(true);
        }
        break;
      case 1:
        if (!reviewIsCounted) {
          sendStats('review');
          setReviewIsCounted(true);
        }
        break;
      case 2:
        if (!webpayIsCounted) {
          sendStats('webpay');
          setWebpayIsCounted(true);
        }
        break;
    }
  }, [activeStep]);

  const handleNext = () => {
    setActiveStep(activeStep + 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleBack = () => {
    trackEvent('Contract Form', 'step_back', `Desde paso ${activeStep + 1}`, activeStep);
    setActiveStep(activeStep - 1);
  };

  const handleFinishFailed = (errorInfo) => {
    trackEvent(
      'Contract Form',
      'validation_error',
      `Paso ${activeStep + 1}`,
      errorInfo.errorFields ? errorInfo.errorFields.length : 0,
    );
    alertError(errorInfo);
  };

  const formContent = (data) => {
    // Track step completion in Google Analytics
    trackEvent('Contract Form', 'step_completed', `Paso ${activeStep + 1}`, activeStep);

    if (activeStep <= 2) {
      updateForm({
        ...contractFormData,
        ...data,
        ...images,
        sourceUrl: sourceUrl,
      });
    };
    if (activeStep <= steps.length) {
      handleNext();
    };
  };

  const steps = [
    {
      title: 'Información firmantes',
      content: <SignersStep form={form} />,
    },
    {
      title: 'Inmueble y condiciones',
      content: <PropertyStep />,
    },
    {
      title: 'Contrato y cuenta',
      content: <AccountStep form={form} />,
    },
    {
      title: 'Revisión',
      content: <FormPreview />,
    },
    {
      title: 'Pago',
      content: <PaymentStep />,
    },
  ];

  const content = (
    <>
      <Content className="ant-layout-content-custom-steps">
        <div className="steps-content">{steps[activeStep].content}</div>
      </Content>
      <CreateContractFooter
        steps={steps}
        activeStep={activeStep}
        handleBack={handleBack}
      />
    </>
  );

  return (
    <React.Fragment>
      <React.Fragment>
        <CreateContractHeader steps={steps} activeStep={activeStep} />
        {activeStep < 4
          ? <Form
            form={form}
            layout="vertical"
            onFinish={formContent}
            onFinishFailed={handleFinishFailed}
            autoComplete="on"
            scrollToFirstError
          >
            {content}
          </Form>
          : <>{content}</>
        }
      </React.Fragment>
      {welcomeModalVisible &&
        <WelcomeForm
          visible={welcomeModalVisible}
          close={() => {
            trackEvent('Contract Form', 'welcome_modal_closed', 'Modal cerrado');
            setWelcomeModalVisible(false);
          }}
        />
      }
    </React.Fragment>
  );
};

CreateContract.propTypes = {
  updateForm: PropTypes.func.isRequired,
  contractFormData: PropTypes.object.isRequired,
  sendStats: PropTypes.func.isRequired,
  getDraft: PropTypes.func.isRequired,
  setSourceUrlInReduce: PropTypes.func.isRequired,
  sourceUrl: PropTypes.string,
  images: PropTypes.object.isRequired,
};

const data = (state) => ({
  contractFormData: state.contract.contractFormData,
  isCreated: state.contract.isCreated,
  images: state.contract.images,
  sourceUrl: state.contract.sourceUrl,
});

const actions = (dispatch) => ({
  updateForm: (data) => dispatch({ type: CONTRACT_FORM_DATA, payload: { ...data } }),
  sendStats: (param) => dispatch({ type: SEND_MONITORING, payload: param }),
  getDraft: (param) => dispatch({ type: GET_DRAFT, payload: { token: param, resource: RESOURCE.GET_DRAFT } }),
  setSourceUrlInReduce: (url) => dispatch({ type: SOURCE_URL, payload: url }),
});

export default connect(data, actions)(CreateContract);
